<script setup lang="ts">
import { computed } from 'vue'
import { useDashboardStore } from '@/store/dashboard'
import { usePortfolioStore } from '@/store/portfolio'
import type { NumericDictionary, BooleanDictionary } from '@/types/common'
import formatClaimsNatureLabel from '@/utils/formatClaimsNatureLabel'
import '../../dashboardTable.css'

const dashboardStore = useDashboardStore()
const portfolioStore = usePortfolioStore()

const emit = defineEmits(['onChangeMargin', 'onChangeTotalMargin', 'onChangeShowColumn', 'onChangeShowColumnTotal'])

const props = defineProps<{
  margin: NumericDictionary;
  showColumn: BooleanDictionary;
  showColumnTotal: boolean;
  totalMargin: number;
  leftColumnSize: number;
  claimsType?: string[];
  visibleColumns?: number[];
  hideTotals?: boolean;
  snapshot?: any;
}>()

const visibleColumns = computed(() => props.visibleColumns || props.snapshot?.visibleColumns || dashboardStore.visibleColumns)
const underwriting_loss_ratios = computed(() => props.snapshot?.underwriting_loss_ratios ?? dashboardStore.underwriting_loss_ratios)
const uw_acc = computed(() => props.snapshot?.uw_acc ?? dashboardStore.dashboards.uw_acc)

function onClickColumn(item: string) {
  emit('onChangeShowColumn', { val: !props.showColumn[item], item: item })
  emit('onChangeMargin', { val: 112 - props.margin[item], item: item })
}

function onClickTotalColumn() {
  emit('onChangeShowColumnTotal', !props.showColumnTotal)
  emit('onChangeTotalMargin', 112 - props.totalMargin)
}

const claimsType = computed<string[]>(() => props.claimsType || portfolioStore.parameters.claims_nature)
</script>
<template>
  <template v-for="(item, index) in claimsType" :key="index">
    <th
      v-if="showColumn[item]"
      class="fixWidth header-teal text-red-500 absolute z-40 cursor-pointer header-sub-column-aligned"
      :style="{
        top: '52px',
        height: '38px',
        left:
          112 * (leftColumnSize + index) -
          dashboardStore.offMarginGWPGEP +
          (<any>Object)
            .values(margin)
            .slice(0, index)
            .reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          'px',
        transition: '0.5s ease-out transform',
        transform: 'translateX(' + margin[item] * 0 + 'px)',
      }"
      @click="onClickColumn(item)"
    >
      Paid
    </th>
    <th
      v-if="showColumn[item]"
      class="fixWidth header-teal text-red-500 absolute z-40 cursor-pointer header-sub-column-aligned"
      :style="{
        top: '52px',
        height: '38px',
        left:
          112 * (leftColumnSize + index) -
          dashboardStore.offMarginGWPGEP +
          (<any>Object)
            .values(margin)
            .slice(0, index)
            .reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          'px',
        transition: '0.5s ease-out transform',
        transform: 'translateX(' + margin[item] * 1 + 'px)',
      }"
      @click="onClickColumn(item)"
    >
      O/S
    </th>
    <th
      v-if="showColumn[item]"
      class="fixWidth header-teal text-red-500 absolute z-40 cursor-pointer header-sub-column-aligned"
      :style="{
        top: '52px',
        height: '38px',
        left:
          112 * (leftColumnSize + index) -
          dashboardStore.offMarginGWPGEP +
          (<any>Object)
            .values(margin)
            .slice(0, index)
            .reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          'px',
        transition: '0.5s ease-out transform',
        transform: 'translateX(' + margin[item] * 2 + 'px)',
      }"
      @click="onClickColumn(item)"
    >
      Incurred
    </th>
    <th
      v-if="visibleColumns?.includes(3) && showColumn[item]"
      class="fixWidth header-teal text-red-500 absolute z-40 cursor-pointer header-sub-column-aligned"
      :style="{
        top: '52px',
        height: '38px',
        left:
          112 * (leftColumnSize + index) -
          dashboardStore.offMarginGWPGEP +
          (<any>Object)
            .values(margin)
            .slice(0, index)
            .reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw' ? 5 : 4) +
          'px',
        transition: '0.5s ease-out transform',
        transform: 'translateX(' + margin[item] * 3 + 'px)',
      }"
      @click="onClickColumn(item)"
    >
      IBNR
    </th>
    <th
      v-if="
        underwriting_loss_ratios == 'Written' &&
        uw_acc == 'uw' &&
        visibleColumns?.includes(3) &&
        showColumn[item]
      "
      class="fixWidth header-teal text-red-500 absolute z-40 cursor-pointer header-sub-column-aligned"
      :style="{
        top: '52px',
        height: '38px',
        left:
          112 * (leftColumnSize + index) -
          dashboardStore.offMarginGWPGEP +
          (<any>Object)
            .values(margin)
            .slice(0, index)
            .reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw' ? 5 : 4) +
          'px',
        transition: '0.5s ease-out transform',
        transform: 'translateX(' + margin[item] * 4 + 'px)',
      }"
      @click="onClickColumn(item)"
    >
      Unearned
    </th>
    <th
      v-if="visibleColumns?.includes(3) && showColumn[item]"
      class="fixWidth header-teal text-red-500 absolute z-40 cursor-pointer header-sub-column-aligned"
      :style="{
        top: '52px',
        height: '38px',
        left:
          112 * (leftColumnSize + index) -
          dashboardStore.offMarginGWPGEP +
          (<any>Object)
            .values(margin)
            .slice(0, index)
            .reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw' ? 5 : 4) +
          'px',
        transition: '0.5s ease-out transform',
        transform:
          'translateX(' +
          margin[item] *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw' ? 5 : 4) +
          'px)',
      }"
      :class="showColumn[item] ? '' : ''"
      @click="onClickColumn(item)"
    >
      Ultimate
    </th>
    <div
      class="fixWidth absolute z-10 text-red-500 header-teal cursor-pointer"
      :class="showColumn[item] ? 'pb-0' : 'pb-0'"
      :style="{
        top: '30px',
        left:
          112 * (leftColumnSize + index) -
          dashboardStore.offMarginGWPGEP +
          (<any>Object)
            .values(margin)
            .slice(0, index)
            .reduce((ps: number, s: number) => ps + s, 0) *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          'px',
        height: showColumn[item] ? '30px' : '60px',
        paddingBottom: '0 !important',
        width:
          112 +
          margin[item] *
            (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
              ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
              : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
          'px',
        transition: '0.5s ease-out all',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        lineHeight: showColumn[item] ? '30px' : '1.2',
      }"
      @click="onClickColumn(item)"
    >
      {{ formatClaimsNatureLabel(item) }}<br v-if="!showColumn[item]" style="margin: 0; line-height: 1.2; display: block;" />{{ !showColumn[item] ? 'Ultimate' : '' }}
    </div>
  </template>

  <th
    v-if="!props.hideTotals && showColumnTotal"
    class="fixWidth header-teal text-red-500 absolute z-10 pt-9 cursor-pointer"
    :style="{
      top: '30px',
      height: '60px',
      left:
        112 * (leftColumnSize + claimsType.length) -
        dashboardStore.offMarginGWPGEP +
        (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
            ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
            : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
        'px',
      transition: '0.5s ease-out all',
      transform: 'translateX(' + totalMargin * 0 + 'px)',
    }"
    @click="onClickTotalColumn"
  >
    Paid
  </th>
  <th
    v-if="!props.hideTotals && showColumnTotal"
    class="fixWidth header-teal text-red-500 absolute z-10 pt-9 cursor-pointer"
    :style="{
      top: '30px',
      height: '60px',
      left:
        112 * (leftColumnSize + claimsType.length) -
        dashboardStore.offMarginGWPGEP +
        (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
            ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
            : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
        'px',
      transition: '0.5s ease-out all',
      transform: 'translateX(' + totalMargin * 1 + 'px)',
    }"
    @click="onClickTotalColumn"
  >
    O/S
  </th>
  <th
    v-if="!props.hideTotals && showColumnTotal"
    class="fixWidth header-teal text-red-500 absolute z-10 pt-9 cursor-pointer"
    :style="{
      top: '30px',
      height: '60px',
      left:
        112 * (leftColumnSize + claimsType.length) -
        dashboardStore.offMarginGWPGEP +
        (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
            ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
            : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
        'px',
      transition: '0.5s ease-out all',
      transform: 'translateX(' + totalMargin * 2 + 'px)',
    }"
    @click="onClickTotalColumn"
  >
    Incurred
  </th>
  <th
    v-if="visibleColumns?.includes(3) && !props.hideTotals && showColumnTotal"
    class="fixWidth header-teal text-red-500 absolute z-10 pt-9 cursor-pointer"
    :style="{
      top: '30px',
      height: '60px',
      left:
        112 * (leftColumnSize + claimsType.length) -
        dashboardStore.offMarginGWPGEP +
        (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw' ? 5 : 4) +
        'px',
      transition: '0.5s ease-out all',
      transform: 'translateX(' + totalMargin * 3 + 'px)',
    }"
    @click="onClickTotalColumn"
  >
    IBNR
  </th>
  <th
    v-if="
      underwriting_loss_ratios == 'Written' &&
      uw_acc == 'uw' &&
      visibleColumns?.includes(3) &&
      !props.hideTotals &&
      showColumnTotal
    "
    class="fixWidth header-teal text-red-500 absolute z-10 pt-9 cursor-pointer"
    :style="{
      top: '30px',
      height: '60px',
      left:
        112 * (leftColumnSize + claimsType.length) -
        dashboardStore.offMarginGWPGEP +
        (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw' ? 5 : 4) +
        'px',
      transition: '0.5s ease-out all',
      transform: 'translateX(' + totalMargin * 4 + 'px)',
    }"
    @click="onClickTotalColumn"
  >
    Unearned
  </th>
  <th
    v-if="visibleColumns?.includes(3) && !props.hideTotals && showColumnTotal"
    class="fixWidth header-teal text-red-500 absolute z-10 cursor-pointer"
    :class="showColumnTotal ? '' : 'pt-2'"
    :style="{
      top: '30px',
      height: '60px',
      left:
        112 * (leftColumnSize + claimsType.length) -
        dashboardStore.offMarginGWPGEP +
        (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw' ? 5 : 4) +
        'px',
      transition: '0.5s ease-out all',
      transform:
        'translateX(' +
        totalMargin *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw' ? 5 : 4) +
        'px)',
    }"
    @click="onClickTotalColumn"
  >
    Ultimate
  </th>
  <div
    v-if="!props.hideTotals"
    class="fixWidth absolute z-20 text-red-500 header-teal cursor-pointer"
    :class="showColumnTotal ? 'pb-0' : 'pb-0'"
    :style="{
      top: '30px',
      left:
        112 * (leftColumnSize + claimsType.length) -
        dashboardStore.offMarginGWPGEP +
        (<any>Object).values(margin).reduce((ps: number, s: number) => ps + s, 0) *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
            ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
            : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
        'px',
      height: showColumnTotal ? '30px' : '60px',
      paddingBottom: '0 !important',
      width:
        112 +
        totalMargin *
          (underwriting_loss_ratios == 'Written' && uw_acc == 'uw'
            ? 5 - (visibleColumns?.includes(3) ? 0 : 3)
            : 4 - (visibleColumns?.includes(3) ? 0 : 2)) +
        'px',
      transition: '0.5s ease-out all',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      lineHeight: showColumnTotal ? '30px' : '1.2',
    }"
    @click="onClickTotalColumn"
  >
    Total<br v-if="!showColumnTotal" style="margin: 0; line-height: 1.2; display: block;" />{{ !showColumnTotal ? 'Ultimate' : '' }}
  </div>
</template>
